import { CubeGlobal } from "./HCViewerUtils";
import type { Box } from "@ts3d-hoops/web-viewer";

/** A mesh vertex as this module handles it: the raw `[x, y, z]` array HOOPS' mesh iterator puts
 *  on `vertex.position`. They survive the JSON round-trip used to de-duplicate edges. */
type Vertex = number[];
/** A closed ring of vertices. */
type Loop = Vertex[];

/**
 * What `findBoundaryLoops` / `classifyLoops` return.
 *
 * KNOWN BUG, preserved verbatim: the early-return branches name the outer ring `outerLoop`
 * while `classifyLoops` names it `outerLoops`, so a caller reading one of them gets `undefined`
 * on the other path. Both keys are declared optional so the mismatch is visible here.
 */
interface BoundaryLoops {
  outerLoop?: Loop | null;
  outerLoops?: (Loop | null)[];
  innerLoops: Loop[];
}

/** The measure markups `getAggregateMeasurementBoundingBox` folds together. */
interface BoundableMeasurement {
  getBoundingBox(): Box;
  select(): void;
}

/**
 * A QTakeOff markup reached through the untyped `CubeGlobal`.
 *
 * KNOWN BUG, preserved verbatim: `addPoint`/`leaderPosition` take a HOOPS `Point3` on the real
 * markup classes, but this module feeds them the raw `[x, y, z]` arrays above.
 */
interface QTakeoffMarkupHandle {
  addPoint(vertex: Vertex): void;
  leaderPosition: Vertex;
  textPosition: Vertex;
  setCalibrationScale?: (scale: number) => void;
  setName(name: string): void;
  finalize(): void;
}

export const getAggregateMeasurementBoundingBox = ({
  measurements,
}: {
  measurements: (BoundableMeasurement | null | undefined)[];
}) => {
  let bounding: Box | undefined;

  measurements.forEach((measurement: BoundableMeasurement | null | undefined, index: number) => {
    if (!bounding) {
      bounding = measurements[index]?.getBoundingBox();
    }
    measurement && measurement.select();
    const box = measurement?.getBoundingBox();
    box && bounding && bounding.addBox(box);
  });
  return bounding;
};

const signedArea = function (loop: Loop) {
  let area = 0;
  const n = loop.length;

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    // Indices are derived from `loop.length`, so the elements are always present.
    area += loop[i]![0]! * loop[j]![1]! - loop[j]![0]! * loop[i]![1]!;
  }

  return area / 2;
};

const classifyLoops = function (loops: Loop[]): BoundaryLoops {
  if (loops.length === 0) return { outerLoops: [], innerLoops: [] };

  // Compute areas and track largest
  let largestLoop: Loop | null = null;
  let largestArea = -Infinity;
  const loopAreas = loops.map((loopObj: Loop) => {
    const area = signedArea(loopObj);
    return { loop: loopObj, area: Math.abs(area) }; // Use absolute area
  });

  // Find the largest loop
  loopAreas.forEach((loopData: { loop: Loop; area: number }) => {
    if (loopData.area > largestArea) {
      largestArea = loopData.area;
      largestLoop = loopData.loop;
    }
  });

  // Classify loops
  const outerLoops = [largestLoop];
  const innerLoops = loopAreas
    .filter((l: { loop: Loop; area: number }) => l.loop !== largestLoop)
    .map((l: { loop: Loop; area: number }) => l.loop);

  return { outerLoops, innerLoops };
};

// Tolerance for floating-point comparison
const TOLERANCE = 1e-6;

// Function to compare two vertices with tolerance
export const compareVertices = function (v1: Vertex, v2: Vertex, tolerance = TOLERANCE) {
  return (
    Math.abs(v1[0]! - v2[0]!) < tolerance &&
    Math.abs(v1[1]! - v2[1]!) < tolerance &&
    Math.abs(v1[2]! - v2[2]!) < tolerance
  );
};

export const findBoundaryLoops = function findBoundaryLoops(vertices: Vertex[]): BoundaryLoops {
  // Step 1: Extract all edges
  const edges: Vertex[][] = [];
  for (let i = 0; i < vertices.length; i += 3) {
    // KNOWN BUG, preserved verbatim: a vertex count that is not a multiple of three leaves
    // `undefined` in the edge list. Erased assertions only.
    const v1 = vertices[i]!;
    const v2 = vertices[i + 1]!;
    const v3 = vertices[i + 2]!;
    edges.push([v1, v2]);
    edges.push([v2, v3]);
    edges.push([v3, v1]);
  }

  // Step 2: Normalize edge representation (sort vertices in each edge)
  const normalizedEdges = edges.map((edge: Vertex[]) =>
    edge.sort((a: Vertex, b: Vertex) => a.toString().localeCompare(b.toString())),
  );

  // Step 3: Count edge occurrences
  const edgeCount = new Map();
  for (const edge of normalizedEdges) {
    const edgeKey = JSON.stringify(edge);
    edgeCount.set(edgeKey, (edgeCount.get(edgeKey) || 0) + 1);
  }

  // Step 4: Identify boundary edges (edges that appear only once)
  const boundaryEdges: Vertex[][] = [];
  for (const [edgeKey, count] of edgeCount.entries()) {
    if (count === 1) {
      boundaryEdges.push(JSON.parse(edgeKey));
    } else if (count > 2) {
      // Non-manifold edge detected (edge shared by more than 2 triangles)
      console.warn("Non-manifold edge detected. Returning empty result.");
      return { outerLoops: [], innerLoops: [] };
    }
  }

  // If no boundary edges, the mesh is closed
  if (boundaryEdges.length === 0) {
    console.warn("No boundary edges found. The mesh is fully closed.");
    return { outerLoops: [], innerLoops: [] };
  }

  // Step 5: Group boundary edges into loops
  const loops: Loop[] = [];
  while (boundaryEdges.length > 0) {
    const startEdge = boundaryEdges.shift();
    if (!startEdge) break;
    // KNOWN BUG, preserved verbatim: a malformed edge shorter than two vertices puts
    // `undefined` into the loop. Erased assertions only.
    const loop: Loop = [startEdge[0]!, startEdge[1]!];
    let currentVertex: Vertex = loop[loop.length - 1]!;
    // Walks connected edges until the loop closes; every exit is a break/return below.
    for (;;) {
      let nextEdge: Vertex[] | null = null;
      let nextEdgeIndex = -1;

      // Find the next edge connected to the current vertex
      for (let i = 0; i < boundaryEdges.length; i++) {
        const edge = boundaryEdges[i];
        if (edge && (compareVertices(edge[0]!, currentVertex) || compareVertices(edge[1]!, currentVertex))) {
          nextEdge = edge;
          nextEdgeIndex = i;
          break;
        }
      }

      if (!nextEdge) {
        // Non-manifold vertex detected (boundary edge not connected properly)
        console.warn("Non-manifold vertex detected. Returning empty result.");
        return { outerLoops: [], innerLoops: [] };
      }

      // Remove the next edge from the boundary edges list
      boundaryEdges.splice(nextEdgeIndex, 1);

      // Add the next vertex to the loop
      const nextVertex = compareVertices(nextEdge[0]!, currentVertex) ? nextEdge[1]! : nextEdge[0]!;
      loop.push(nextVertex);
      currentVertex = nextVertex;

      // Check if the loop is closed
      if (compareVertices(currentVertex, loop[0]!)) {
        loops.push(loop);
        break;
      }
    }
  }
  const classifiledLoops = classifyLoops(loops);
  return classifiledLoops;
};

export const fillMeasurementPointsAndFinalize = function (markup: QTakeoffMarkupHandle, loop: Loop) {
  for (const v of loop) {
    markup.addPoint(v);
  }
  markup.leaderPosition = loop[0]!;
  markup.textPosition = markup.leaderPosition;
  if (markup.setCalibrationScale) markup.setCalibrationScale(CubeGlobal.calibrationScale);

  markup.finalize();
  // `viewerImpl` is now declared `ViewerImpl | null` (it is null until BottomToolbar mounts a
  // viewer) rather than `any`. Every path into this module runs from a live viewer interaction, so
  // these are erased assertions and change nothing at runtime — a guard would turn today's throw
  // into a silent no-op. Same for the `viewerImpl!` uses below.
  viewerImpl!.hwv.measureManager.addMeasurement(markup);
  viewerImpl!.hwv.measureManager.finalizeMeasurement(markup);
};

export const createMeasuremntMarkup = function (operatorId: number, loops: BoundaryLoops) {
  let markup: QTakeoffMarkupHandle | null = null;
  // See the `outerLoop` / `outerLoops` note on BoundaryLoops: this reads the plural key, which
  // only the `classifyLoops` path sets. Erased assertion; a guard would change behaviour.
  const loopsArray = [...loops.innerLoops, ...loops.outerLoops!];

  switch (operatorId) {
    case CubeGlobal.OperatorId.QTakeOffAreaOperator:
    case CubeGlobal.OperatorId.QTakeOffFreeHandAreaOperator:
      for (const loop of loopsArray) {
        markup = new CubeGlobal.QTakeOffAreaMarkup(viewerImpl!.hwv);
        if (markup) {
          markup.setName("QTakeOffAreaMarkup");
          fillMeasurementPointsAndFinalize(markup, loop!);
        }
      }
      break;
    case CubeGlobal.OperatorId.QTakeOffPerimeterOperator:
      for (const loop of loopsArray) {
        markup = new CubeGlobal.QTakeOffLengthMarkup(viewerImpl!.hwv);
        if (markup) {
          markup.setName("QTakeOffLengthMarkup");
          fillMeasurementPointsAndFinalize(markup, loop!);
        }
      }
      break;
    default:
      break;
  }
  return markup;
};
